'use client'

import type { ReactNode } from 'react'
import { useSyncExternalStore } from 'react'
import type { Episode, Media, Upload } from '@/payload-types'

import Link from 'next/link'
import { Heading } from '@systhemaui/react'
import { Image, Chip } from '@systhemaui/next'

const emptySubscribe = () => () => {}
// Capture timestamp once at module load — stable reference for useSyncExternalStore
const clientNow = typeof window !== 'undefined' ? Date.now() : 0

export type EpisodeItemProps = {
  episode: Episode
  isFirst?: boolean
  isSignedIn?: boolean
  slug?: string
  descriptionJSX?: ReactNode
}

export function EpisodeItem({
  episode,
  isFirst,
  isSignedIn = false,
  slug,
  descriptionJSX,
}: EpisodeItemProps) {
  // Stable client timestamp — no flash, applied synchronously during hydration.
  // clientNow is captured once at module load so the snapshot never changes between renders.
  const now = useSyncExternalStore(
    emptySubscribe,
    () => clientNow,
    () => 0,
  )

  const media = typeof episode.media === 'number' ? null : (episode.media as Media)
  const mediaUuid = media?.uuid
  const embed =
    episode.embed && typeof episode.embed === 'object' && 'uuid' in episode.embed
      ? episode.embed
      : null
  const embedUuid = embed?.uuid as string | undefined
  const isStream = episode.sourceType === 'stream'
  const contentUuid = isStream ? embedUuid : mediaUuid
  const contentRoute = isStream ? 'stream' : 'video'
  const loginUrl = slug ? `/belepes?redirect=${encodeURIComponent(`/musor/${slug}`)}` : '/belepes'
  const backHref = slug ? `/musor/${slug}` : undefined

  // When now=0 (server): makeAvailableAfter is always > 0, so isComingSoon = raw flag (conservative)
  // When now=Date.now() (client): real date check is performed
  const isComingSoon =
    (episode.isComingSoon ?? false) &&
    !(episode.makeAvailableAfter && new Date(episode.makeAvailableAfter).getTime() <= now)

  // LIVE: active if checkbox is on AND liveUntil hasn't passed (or no liveUntil set)
  const isLive =
    !isComingSoon &&
    (episode.isLive ?? false) &&
    !(episode.liveUntil && new Date(episode.liveUntil).getTime() <= now)

  const videoUrl = isComingSoon
    ? undefined
    : isSignedIn
      ? contentUuid
        ? `/${contentRoute}/${contentUuid}${backHref ? `?backHref=${encodeURIComponent(backHref)}` : ''}`
        : '#'
      : loginUrl

  const comingSoonThumb =
    typeof episode.comingSoonThumbnail === 'number'
      ? null
      : (episode.comingSoonThumbnail as Upload | undefined | null)
  const regularThumb = typeof episode.thumbnail === 'number' ? null : (episode.thumbnail as Upload)
  const thumbnailUrl =
    isComingSoon && comingSoonThumb?.url ? comingSoonThumb.url : regularThumb?.url || ''

  const wrapperClassName =
    'group/episode-card flex w-full flex-col items-start gap-(--gap-sm) md:flex-row md:items-center'

  const content = (
    <>
      {/* Episode thumbnail */}
      <div
        className={`${isComingSoon ? 'bg-black' : 'image-skeleton'} relative aspect-video w-full shrink-0 overflow-hidden border border-transparent shadow-[-1px_-1px_0_0_rgba(102,102,102,0.25),1px_1px_0_0_rgba(255,255,255,0.25)] md:aspect-auto md:h-37 md:w-65.75 ${videoUrl ? 'transition-colors duration-300 group-hover/episode-card:border-white' : ''} ${
          isFirst
            ? 'shadow-[0px_0px_0px_2px_var(--layout-bg-base),0px_0px_0px_4px_var(--primitives-white)]'
            : ''
        }`}
      >
        {thumbnailUrl ? (
          <Image
            src={thumbnailUrl}
            alt={episode.title}
            objectFit="cover"
            className={`size-full ${videoUrl ? 'transition-opacity duration-300 group-hover/episode-card:opacity-50' : ''} ${isComingSoon ? 'opacity-30' : ''}`}
          />
        ) : (
          <div
            className={`flex size-full items-center justify-center bg-(--layout-bg-muted) ${videoUrl ? 'transition-opacity duration-300 group-hover/episode-card:opacity-50' : ''} ${isComingSoon ? 'opacity-30' : ''}`}
          >
            <span className="material-symbols-outlined text-4xl opacity-40">movie</span>
          </div>
        )}
        {isComingSoon && (
          <div className="absolute inset-0 flex items-center justify-center bg-(--color-media-overlay)">
            <span className="text-sm font-semibold tracking-wide text-white">
              {episode.comingSoonText || 'Hamarosan...'}
            </span>
          </div>
        )}
      </div>

      {/* Episode info */}
      <div className="flex min-w-0 flex-1 flex-col gap-2.5 text-(--typography-body)">
        {(episode.label || isComingSoon || isLive || (episode.isNew && !isComingSoon && !isLive)) &&
          (() => {
            // Priority: Hamarosan > ÉLŐ > Új rész (only one badge at a time)
            const badge = isComingSoon
              ? episode.comingSoonText
                ? 'comingSoon'
                : null
              : isLive
                ? 'live'
                : episode.isNew
                  ? 'new'
                  : null
            return (
              <div
                className={`flex gap-2 md:items-center ${badge ? 'max-md:flex-col max-md:items-start' : ''}`}
              >
                {badge && (
                  <div className="flex items-center gap-x-2">
                    {badge === 'comingSoon' && (
                      <Chip.span className="text-small shrink-0 rounded-none bg-gray-500 px-2 py-1 leading-none font-semibold uppercase">
                        Hamarosan
                      </Chip.span>
                    )}
                    {badge === 'live' && (
                      <Chip.span className="text-small shrink-0 rounded-none bg-green-600 px-2 py-1 leading-none font-semibold uppercase">
                        <span className="mr-0.5 inline-block size-2 animate-live-dot rounded-full bg-white" />
                        ÉLŐ
                      </Chip.span>
                    )}
                    {badge === 'new' && (
                      <Chip.span className="text-small shrink-0 rounded-none bg-red-500 px-2 py-1 leading-none font-semibold uppercase">
                        Új rész
                      </Chip.span>
                    )}
                  </div>
                )}
                {episode.label && <p className="text-small color-body">{episode.label}</p>}
              </div>
            )
          })()}
        <Heading.h6>{episode.title}</Heading.h6>
        {descriptionJSX && (
          <div className="line-clamp-3 text-sm leading-6 opacity-80">{descriptionJSX}</div>
        )}
      </div>
    </>
  )

  if (!videoUrl) {
    return <div className={wrapperClassName}>{content}</div>
  }

  return (
    <Link href={videoUrl} className={wrapperClassName}>
      {content}
    </Link>
  )
}
